/*
 * Copyright 2024 GLA UK Research and Development Directive
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { KeycloakOptions, KeycloakService } from 'keycloak-angular';
import { DomainService } from 'src/app/modules/shared/services/domain.service';
import { LanguageService } from 'src/app/modules/shared/services/language.service';
import { environment } from 'src/environments/environment';


/**
 * The application initialiser which creating the keycloak client.
 * 
 * This is where the initial operations of the app will take place, just
 * before the application boostraping operations.
 * 
 * @param keycloak        The keycloak service
 * @param domainService   The domain service
 * @param languageService The language service
 */
export function initializer(keycloak: KeycloakService,
                            domainService: DomainService,
                            languageService: LanguageService) {
    // Add a debug line
    console.log('APP_INITIALIZER is called');

    // Determine the keycloak initialisation informaton
    const keycloakConfig: KeycloakOptions = {
        config: {
            url: environment.keycloakConfig.authServerUrl,
            realm: environment.keycloakConfig.realm,
            clientId: environment.keycloakConfig.clientId
        },
        initOptions: {
            onLoad: 'check-sso',
            checkLoginIframe: true,
        },
        bearerExcludedUrls: []
    }
    
    // And initialise the app
    return () => domainService
            // First initialise the domain
            .initDomain()
            // Then initialise keycloak
            .then(() =>keycloak.init(keycloakConfig))
            // Finally initialise the languages
            .finally(() => languageService.initLanguage());
}